/**
 * A room: the lobby, the share sheet, and the table.
 *
 * Everything here is a thin shell over `RoomClient`. The one rule to keep is
 * that a `view` from the server replaces state wholesale — this component holds
 * no derived copy of the game, so there is nothing to drift.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import QRCode from 'qrcode';
import type { GameAction } from '@shared/engine';
import { MAX_PLAYERS, MIN_PLAYERS, NAME_MAX, sanitizeText, type RoomView } from '@shared/protocol';
import { Table } from '../game/Table';
import { RoomClient, errorText, hasToken, storedName, type ConnStatus } from '../net/socket';
import { Screen, Toast, useAnnounce } from '../ui/shell';

export function RemoteGame({
  joinCode,
  onExit,
  onNewRoom,
}: {
  joinCode: string | null;
  onExit: () => void;
  onNewRoom: () => void;
}) {
  /**
   * A held token plus a remembered name means this browser already has a seat in
   * this room, so go straight back to it. Without this, refreshing the page —
   * or coming back to a backgrounded tab that Safari discarded — drops you on a
   * name screen in the middle of a round you are still sitting in.
   */
  const resuming = !!joinCode && hasToken(joinCode) && !!storedName();

  const [status, setStatus] = useState<ConnStatus>(
    resuming
      ? { kind: 'connecting' }
      : { kind: 'naming', code: joinCode, error: null, suggestion: null },
  );
  const [view, setView] = useState<RoomView | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const announce = useAnnounce();
  const clientRef = useRef<RoomClient | null>(null);

  if (!clientRef.current) {
    clientRef.current = new RoomClient({
      onView: setView,
      onStatus: setStatus,
      onError: (_code, message) => setToast(message),
      onJoined: (code) => {
        // Put the code in the URL so a refresh rejoins rather than starting over.
        if (location.hash !== `#/${code}`) history.replaceState(null, '', `#/${code}`);
      },
    });
  }
  const client = clientRef.current;

  useEffect(() => () => client.close(), [client]);

  // Reclaim the seat, once, on mount. The server's token path bypasses every
  // join check, so this works mid-game and even while the room is "full".
  useEffect(() => {
    if (resuming && joinCode) client.begin('join', storedName(), joinCode);
    // Deliberately mount-only: `begin` opens a socket, and re-running it on any
    // dependency change would open a second one and replace the seat it just took.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (view?.hostChangedTo) announce(`${view.hostChangedTo} is now the host.`);
  }, [view?.hostChangedTo, announce]);

  const act = useCallback(
    (action: GameAction) => {
      switch (action.t) {
        case 'draw':
          client.send({ t: 'draw', region: action.region });
          return;
        case 'play':
          client.send({ t: 'play', chip: action.chip });
          return;
        case 'unplay':
          client.send({ t: 'unplay' });
          return;
        case 'answer':
          client.send({ t: 'answer', pendingId: action.pendingId, answer: action.answer });
          return;
      }
    },
    [client],
  );

  if (status.kind === 'naming') {
    return (
      <NameScreen
        status={status}
        onExit={onExit}
        onSubmit={(name) => client.begin(status.code ? 'join' : 'create', name, status.code)}
      />
    );
  }

  if (status.kind === 'ended') {
    return <EndedScreen reason={status.reason} onExit={onExit} onNewRoom={onNewRoom} />;
  }

  if (status.kind === 'waitingForNextGame') {
    return (
      <Screen eyebrow="Room" title="That game is already under way" hot>
        <p>
          You will be let in the moment they finish. Keep this open — it retries on its own every ten
          seconds.
        </p>
        <p className="muted">Room {status.code}</p>
      </Screen>
    );
  }

  if (!view) {
    return (
      <Screen eyebrow="Room" title={status.kind === 'connecting' ? 'Connecting' : 'Reconnecting'}>
        <p className="muted">One moment.</p>
      </Screen>
    );
  }

  const banner = <ConnBanner status={status} view={view} client={client} />;

  if (!view.game) {
    return <Lobby view={view} client={client} banner={banner} onExit={onExit} toast={toast} clearToast={() => setToast(null)} />;
  }

  return (
    <>
      <Table
        view={view.game}
        act={act}
        banner={banner}
        onExit={onExit}
        endActions={
          view.you.mayStart ? (
            <button type="button" className="btn" onClick={() => client.send({ t: 'again' })}>
              Back to the lobby
            </button>
          ) : (
            <p className="muted">Waiting for the host to deal again.</p>
          )
        }
      />
      <Toast text={toast} onDone={() => setToast(null)} />
    </>
  );
}

// ── connection chrome ───────────────────────────────────────────────────────

function ConnBanner({ status, view, client }: { status: ConnStatus; view: RoomView; client: RoomClient }) {
  if (status.kind === 'restarting') {
    return <div className="banner banner--warn">The game server is restarting. Your seat is held.</div>;
  }
  if (status.kind === 'reconnecting') {
    return <div className="banner banner--warn">Reconnecting…</div>;
  }
  // The host's escape hatch, and the only thing that unsticks a table when
  // somebody's phone dies mid-round.
  if (view.hostAction) {
    return (
      <div className="banner banner--warn">
        <span>
          {view.hostAction.names.join(' and ')} {view.hostAction.names.length === 1 ? 'has' : 'have'} dropped.
        </span>
        <button type="button" className="linkish" onClick={() => client.send({ t: 'kick', playerId: '' })}>
          Let the computer play for them
        </button>
      </div>
    );
  }
  if (view.hostChangedTo) {
    return <div className="banner">{view.hostChangedTo} is now the host.</div>;
  }
  return null;
}

// ── screens ─────────────────────────────────────────────────────────────────

function NameScreen({
  status,
  onSubmit,
  onExit,
}: {
  status: Extract<ConnStatus, { kind: 'naming' }>;
  onSubmit: (name: string) => void;
  onExit: () => void;
}) {
  const [name, setName] = useState(() => status.suggestion ?? storedName());
  const clean = sanitizeText(name).slice(0, NAME_MAX);
  const ready = clean.length > 0;

  return (
    <Screen
      eyebrow={status.code ? `Joining ${status.code}` : 'New room'}
      title={status.code ? 'What shall we call you?' : 'Start a room'}
      actions={
        <>
          <button type="button" className="btn" disabled={!ready} onClick={() => onSubmit(clean)}>
            {status.code ? 'Join the table' : 'Create the room'}
          </button>
          <button type="button" className="btn ghost" onClick={onExit}>
            Back
          </button>
        </>
      }
    >
      {status.error ? <p className="error-note">{errorText(status.error)}</p> : null}
      <label className="field">
        <span className="field__label">Your name</span>
        <input
          className="field__input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={NAME_MAX}
          autoComplete="nickname"
          enterKeyHint="go"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && ready) onSubmit(clean);
          }}
        />
      </label>
      <p className="muted">Everyone at the table sees this.</p>
    </Screen>
  );
}

function Lobby({
  view,
  client,
  banner,
  onExit,
  toast,
  clearToast,
}: {
  view: RoomView;
  client: RoomClient;
  banner: React.ReactNode;
  onExit: () => void;
  toast: string | null;
  clearToast: () => void;
}) {
  const [bots, setBots] = useState(0);
  const humans = view.players.filter((p) => !p.isBot).length;
  const total = humans + bots;
  const canStart = view.you.isHost && total >= MIN_PLAYERS && total <= MAX_PLAYERS;
  const maxBots = Math.max(0, MAX_PLAYERS - humans);

  useEffect(() => {
    if (bots > maxBots) setBots(maxBots);
  }, [bots, maxBots]);

  return (
    <>
      <Screen
        eyebrow={`Room ${view.code}`}
        title="The table is forming"
        actions={
          view.you.isHost ? (
            <>
              <button
                type="button"
                className="btn"
                disabled={!canStart}
                onClick={() => client.send({ t: 'start', bots })}
              >
                {total < MIN_PLAYERS ? 'Two players minimum' : `Deal for ${total}`}
              </button>
              <button type="button" className="btn ghost" onClick={onExit}>
                Leave
              </button>
            </>
          ) : (
            <>
              <p className="muted">Waiting for the host to deal.</p>
              <button type="button" className="btn ghost" onClick={onExit}>
                Leave
              </button>
            </>
          )
        }
      >
        {banner}
        <Share code={view.code} />

        <section aria-label="Players">
          <h2 className="section-title">
            At the table <span className="muted">({total})</span>
          </h2>
          <ul className="lobby-list">
            {view.players.map((p) => (
              <li key={p.id} className={p.online ? 'lobby-row' : 'lobby-row lobby-row--away'}>
                <span>
                  {p.isHost ? <span aria-hidden="true">♔ </span> : null}
                  {p.name}
                  {p.id === view.you.id ? ' (you)' : ''}
                </span>
                <span className="lobby-row__meta">
                  {p.online ? null : <span className="tag">away</span>}
                  {view.you.isHost && p.id !== view.you.id && !p.isBot ? (
                    <button
                      type="button"
                      className="linkish"
                      onClick={() => client.send({ t: 'kick', playerId: p.id })}
                    >
                      remove
                    </button>
                  ) : null}
                </span>
              </li>
            ))}
            {Array.from({ length: bots }, (_, i) => (
              <li key={`bot-${i}`} className="lobby-row lobby-row--bot">
                <span>Computer {i + 1}</span>
                <span className="tag">bot</span>
              </li>
            ))}
          </ul>
        </section>

        {view.you.isHost ? (
          <section aria-label="Computer players">
            <h2 className="section-title">Fill the table</h2>
            <p className="muted">
              Two is a real game, but Excalibur breathes at four or more. Add computer players to make
              up the numbers.
            </p>
            <div className="stepper">
              <button
                type="button"
                className="stepper__btn"
                onClick={() => setBots((n) => Math.max(0, n - 1))}
                disabled={bots === 0}
                aria-label="One fewer computer player"
              >
                −
              </button>
              <span className="stepper__value" aria-live="polite">
                {bots} computer {bots === 1 ? 'player' : 'players'}
              </span>
              <button
                type="button"
                className="stepper__btn"
                onClick={() => setBots((n) => Math.min(maxBots, n + 1))}
                disabled={bots >= maxBots}
                aria-label="One more computer player"
              >
                +
              </button>
            </div>
          </section>
        ) : null}
      </Screen>
      <Toast text={toast} onDone={clearToast} />
    </>
  );
}

/**
 * The share sheet.
 *
 * The QR goes through `toDataURL`, never `toString` into `dangerouslySetInnerHTML`
 * — an SVG string injected as markup is a script-execution surface, and the CSP
 * would not save a page that put it there itself.
 */
function Share({ code }: { code: string }) {
  const [qr, setQr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const url = useMemo(() => `${location.origin}/#/${code}`, [code]);

  useEffect(() => {
    let alive = true;
    QRCode.toDataURL(url, { margin: 1, width: 320, color: { dark: '#14110c', light: '#efe4c8' } })
      .then((data) => {
        if (alive) setQr(data);
      })
      .catch(() => {
        /* the code and the link are enough on their own */
      });
    return () => {
      alive = false;
    };
  }, [url]);

  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Excalibur', text: 'Come and play Excalibur', url });
        return;
      }
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* the user dismissed the sheet, or the clipboard is unavailable */
    }
  };

  return (
    <section className="share" aria-label="Invite">
      <p className="share__code" aria-label={`Room code ${code.split('').join(' ')}`}>
        {code}
      </p>
      {qr ? <img className="share__qr" src={qr} alt={`QR code linking to room ${code}`} width={160} height={160} /> : null}
      <button type="button" className="btn ghost" onClick={share}>
        {copied ? 'Link copied' : 'Send the link'}
      </button>
    </section>
  );
}

function EndedScreen({
  reason,
  onExit,
  onNewRoom,
}: {
  reason: 'removed' | 'roomGone' | 'replaced';
  onExit: () => void;
  onNewRoom: () => void;
}) {
  const copy = {
    removed: {
      title: 'You were removed from the room',
      body: 'The host took your seat off the table. You can start a room of your own.',
    },
    roomGone: {
      title: 'That room is gone',
      body: 'Rooms are held in memory and are cleared when they go quiet, or when the server restarts.',
    },
    replaced: {
      title: 'You opened this room somewhere else',
      body: 'A seat can only be in one place. This tab has let go of it — carry on in the other one.',
    },
  }[reason];

  return (
    <Screen
      eyebrow="Room"
      title={copy.title}
      actions={
        <>
          <button type="button" className="btn" onClick={onNewRoom}>
            Start a new room
          </button>
          <button type="button" className="btn ghost" onClick={onExit}>
            Back to the front
          </button>
        </>
      }
    >
      <p>{copy.body}</p>
    </Screen>
  );
}
